import React from 'react';
import PropTypes from 'prop-types';
import { VariableSizeList } from 'react-window';

import MuiAutocomplete, {
  createFilterOptions,
} from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import ListSubheader from '@mui/material/ListSubheader';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled, useTheme } from '@mui/material/styles';

const filter = createFilterOptions();

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) =>
    prop !== 'backgroundColor' && prop !== 'multiple',
})(({ backgroundColor, multiple, theme }) => ({
  width: '100%',
  height: multiple ? 'auto' : 40,
  minHeight: 40,
  background: backgroundColor || 'var(--color-surface)',
  borderRadius: 8,

  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    borderRadius: 8,
    background: backgroundColor || 'var(--color-surface)',
    padding: multiple ? '4px 4px' : 0,

    '& fieldset': {
      borderColor: 'var(--color-border)',
    },

    '&:hover fieldset': {
      borderColor: 'var(--color-border)',
    },

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
  },
}));

const StyledOutlinedInput = styled(OutlinedInput, {
  shouldForwardProp: (prop) => prop !== 'multiple',
})(({ multiple }) => ({
  width: '100%',
  padding: '0 6px !important',

  '& .MuiAutocomplete-input': {
    fontSize: 14,
    color: 'var(--color-text-primary)',
    minWidth: 60,
  },

  '&.Mui-disabled .MuiAutocomplete-input': {
    WebkitTextFillColor: 'var(--color-text-muted)',
  },
}));

const StyledAutocomplete = styled(MuiAutocomplete)(() => ({
  '& .MuiAutocomplete-endAdornment': {
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
  },

  '& .MuiAutocomplete-popupIndicator': {
    color: 'var(--color-text-muted)',
  },

  '& .MuiAutocomplete-clearIndicator': {
    display: 'none',
  },
}));

const StyledOption = styled('li')(() => ({
  padding: '6px 10px',
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  color: 'var(--color-text-primary)',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: 13,
  borderRadius: 6,
  backgroundColor: theme.palette.mode === 'dark' ? 'var(--color-hover-surface)' : '#f1f1f1',
  color: theme.palette.text.primary,

  '& .MuiChip-deleteIcon': {
    fontSize: 16,
  },
}));

const LISTBOX_PADDING = 8;

const renderRow = ({ data, index, style }) =>
  React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) =>
    React.isValidElement(child) && child.type === ListSubheader
      ? 48
      : itemSize;

  const getHeight = () =>
    itemData.length > 8
      ? 8 * itemSize
      : itemData.map(getChildSize).reduce((a, b) => a + b, 0);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          key={itemData.length}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemData.length}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const renderGroup = (params) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

const Autocomplete = (props) => {
  const {
    value,
    onChange,
    options = [],
    getOptionLabel = (option) => option.label ?? option,
    groupBy,
    label,
    required,
    disabled = false,
    backgroundColor,
    placeholder = 'Select',
    multiple = false,
    allowCreate = false,
    confirmOnRemove = false,
    onConfirmRemove,
    inputValue,
    onInputChange,
    ...rest
  } = props;

  const controlledInputProps =
    typeof inputValue === 'string' && typeof onInputChange === 'function'
      ? { inputValue, onInputChange }
      : {};

  return (
    <>
      {!!label && (
        <Box sx={{ display: 'flex', marginBottom: '2px' }}>
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
          {required && (
            <Typography sx={(theme) => ({ fontSize: 16, ml: '1px', color: theme.palette.primary.main })}>
              *
            </Typography>
          )}
        </Box>
      )}

      <StyledFormControl
        backgroundColor={backgroundColor}
        multiple={multiple}
      >
        <StyledAutocomplete
          multiple={multiple}
          freeSolo={allowCreate}
          disabled={disabled}
          options={options}
          value={value}
          groupBy={groupBy}
          popupIcon={<KeyboardArrowDownIcon />}
          slotProps={{
            paper: {
              sx: (theme) => ({
                backgroundColor: 'var(--color-surface-elevated)',
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
              }),
            },
          }}
          ListboxComponent={ListboxComponent}
          renderGroup={renderGroup}
          filterSelectedOptions={multiple}
          filterOptions={(opts, params) => {
            const filtered = filter(opts, params);

            if (!allowCreate) return filtered;

            const { inputValue } = params;

            const exists = opts.some(
              (option) =>
                getOptionLabel(option)
                  .toLowerCase()
                  .trim() === inputValue.toLowerCase().trim()
            );

            if (inputValue !== '' && !exists) {
              filtered.push({
                inputValue,
                label: `Add "${inputValue}"`,
              });
            }

            return filtered;
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (option.inputValue) return option.inputValue;
            return getOptionLabel(option);
          }}
          onChange={async (e, newValue, reason, details) => {
            if (reason === 'removeOption' && confirmOnRemove) {
              const removedOption = details?.option;

              let confirmed = true;

              if (onConfirmRemove) {
                confirmed = await onConfirmRemove(removedOption);
              }

              if (!confirmed) {
                return;
              }
            }

            if (reason === 'clear' && confirmOnRemove) {
              let confirmed = true;

              if (onConfirmRemove) {
                confirmed = await onConfirmRemove(null, 'clear');
              }

              if (!confirmed) return;
            }

            if (!allowCreate) {
              onChange(newValue);
              return;
            }

            const normalized = newValue.map((item) => {
              if (typeof item === 'string') return { label: item };
              if (item.inputValue) return { label: item.inputValue };
              return item;
            });

            onChange(normalized);
          }}
          renderOption={(props, option) => (
            <StyledOption {...props}>
              {option.label ?? getOptionLabel(option)}
            </StyledOption>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <StyledChip
                label={getOptionLabel(option)}
                {...getTagProps({ index })}
                key={index}
              />
            ))
          }
          isOptionEqualToValue={(option, value) =>
            getOptionLabel(option) === getOptionLabel(value)
          }
          renderInput={(params) => (
            <StyledOutlinedInput
              {...params.InputProps}
              inputProps={params.inputProps}
              placeholder={multiple && value?.length ? '' : placeholder}
              multiple={multiple}
            />
          )}
          {...controlledInputProps}
          {...rest}
        />
      </StyledFormControl>
    </>
  );
};

Autocomplete.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  groupBy: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  backgroundColor: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  allowCreate: PropTypes.bool,
};

export default Autocomplete;
